import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { getSectionReportCards, type SectionReportCards, type SectionCardStudent } from "../../../service/report-card"
import { rcAssetUrlsApi } from "../../../service/report-card"
import { all_routes } from "../../router/all_routes"
import { renderPrintLayer, planSubjects } from "./PrintLayer"
import { campusHeaderSource, isRmsType, type RmsTpl } from "../report-card-designer/canvas/rmsRender"

const rmsTplOf = (data: SectionReportCards): RmsTpl => ({
  schoolName: data.school.name,
  logoKey: data.school.logoKey,
  headerImageKey: data.school.headerImageKey,
})

// Picker → batch sheet — every student in the section rendered as one A4
// (or the template's own canvas size) page, stacked for one print job.
// Mirrors RMS's ReportCards.jsx SheetView exactly (Open/Print bar on the
// picker, one page per student here, page-break-after between students).
const ReportCardSheet = () => {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [data, setData] = useState<SectionReportCards | null>(null)
  const [loading, setLoading] = useState(true)
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({})
  const assetUrlsRef = useRef<Record<string, string>>({})
  useEffect(() => { assetUrlsRef.current = assetUrls }, [assetUrls])
  const fetchingRef = useRef<Set<string>>(new Set())

  const breakdown = params.get("breakdown") || ""
  const classId = params.get("class") || ""
  const section = params.get("section") || ""
  const autoprint = params.get("print") === "1"

  useEffect(() => {
    if (!breakdown || !classId || !section) return
    setLoading(true)
    getSectionReportCards({ breakdown, class: classId, section })
      .then(r => setData(r.data))
      .catch((e: any) => toast.error(e?.response?.data?.message || "Failed to load report cards"))
      .finally(() => setLoading(false))
  }, [breakdown, classId, section])

  // Fallback for any key discovered after the initial batch (shouldn't
  // normally happen — every key is known up front from `data` — but keeps
  // renderPrintLayer's onNeedUrl contract safe).
  const ensureAssetUrl = useCallback(async (key: string) => {
    if (!key || assetUrlsRef.current[key] || fetchingRef.current.has(key)) return
    fetchingRef.current.add(key)
    try {
      const map = await rcAssetUrlsApi([key])
      if (map[key]) setAssetUrls(m => ({ ...m, [key]: map[key] }))
    } catch { /* asset optional */ }
    finally { fetchingRef.current.delete(key) }
  }, [])

  // Resolve every student's photo + the campus header image + any image
  // layers in ONE batched request instead of one round trip per asset key —
  // a richly-imported template can have a dozen+ image layers, and resolving
  // them individually was the real cause of the slow initial load.
  const neededAssetKeys = (() => {
    if (!data) return [] as string[]
    const keys = new Set<string>()
    data.students.forEach(st => { if (st.photo) keys.add(st.photo) })
    if (data.school.headerImageKey) keys.add(data.school.headerImageKey)
    if (data.school.logoKey) keys.add(data.school.logoKey)
    const tpl = rmsTplOf(data)
    for (const l of data.template?.definition?.layers || []) {
      if (l.type === "image" && l.src) keys.add(l.src)
      if (l.type === "rms_campus_header") {
        const key = campusHeaderSource({ ...((l as any)._rmsProps || {}), ...l }, tpl).key
        if (key) keys.add(key)
      }
    }
    return Array.from(keys)
  })()

  useEffect(() => {
    if (!neededAssetKeys.length) return
    rcAssetUrlsApi(neededAssetKeys).then(map => setAssetUrls(m => ({ ...m, ...map }))).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Logo/photo circles printed blank even though the "Open" (non-print) view
  // showed them correctly: the Print button and autoprint timer only waited
  // for `data` (the template/marks structure), not for the asset-URL batch
  // above to actually finish -- a real network round trip that 400ms isn't
  // reliably longer than. assetsReady now requires every key this template
  // actually needs to be present in assetUrls before printing is allowed.
  const assetsReady = neededAssetKeys.every(k => !!assetUrls[k])

  useEffect(() => {
    if (autoprint && data && !loading && assetsReady) {
      const t = setTimeout(() => window.print(), 400)
      return () => clearTimeout(t)
    }
  }, [autoprint, data, loading, assetsReady])

  if (!breakdown || !classId || !section) {
    return <div className="p-4 text-muted">Missing breakdown, class or section in the URL.</div>
  }

  // Render in the template's own canvas px units throughout — `@page { size:
  // Npx Mpx }` tells Chrome's print engine to map that CSS-px canvas onto a
  // physically-correct page size, same trick already used by the marks-
  // tabulation sheet and the server-side report-card PDF renderer. No mm
  // conversion needed (and the earlier version of this file had a real bug
  // doing that conversion at the layer level — font sizes ended up roughly
  // 4x too small relative to the page).
  const canvas = data?.template?.definition?.canvas || { width: 794, height: 1123, background: "#fffaf0" }
  const layers = data?.template?.definition?.layers || []

  return (
    <div className="page-wrapper">
      <div className="content">
        <style>{`
          @media print {
            @page { size: ${canvas.width}px ${canvas.height}px; margin: 0; }
            /* The app-wide print rule (_print.scss) hides body* and only shows .printable-content
               — without this wrapper every layer here is invisible and only body's own background
               (black in dark mode) is left to print, producing N blank/black pages. */
            html, body { background: #fff !important; }
            /* Chrome drops background-color/gradient fills on print by default
               (ink-saving default) unless told to keep them -- every subject
               card header, grade badge, progress bar and banner here is an
               inline background colour, so without this the whole sheet
               printed as flat grey/white boxes with no colour at all, even
               though the on-screen "Open" view renders fully correctly. */
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
          }
          .rc-sheet-page { width: ${canvas.width}px; height: ${canvas.height}px; margin: 8mm auto; background: ${canvas.background || "#fffaf0"}; box-shadow: 0 4px 16px rgba(0,0,0,0.08); page-break-after: always; position: relative; overflow: hidden; }
          .rc-sheet-page:last-child { page-break-after: auto; }
          @media print { .rc-sheet-page { margin: 0 auto; box-shadow: none; } }
        `}</style>
        <div className="d-flex align-items-center justify-content-between mb-3 no-print">
          <button className="btn btn-light btn-sm" onClick={() => navigate(all_routes.reportCard)}>← Back to picker</button>
          <div className="text-muted small">
            {data ? `${data.breakdown.title} · ${data.class.name} · Section ${data.section.name}` : ""}
          </div>
          <button className="btn btn-dark btn-sm" onClick={() => window.print()} disabled={!data || !assetsReady}>Print</button>
        </div>

        <div className="printable-content">
          {loading ? (
            <div className="text-center text-muted py-5">Building report cards…</div>
          ) : !data ? (
            <div className="text-center text-muted py-5">Could not load report cards.</div>
          ) : !layers.length ? (
            <div className="text-center text-muted py-5 border rounded">
              No report card design exists yet for {data.class.name} ({data.breakdown.title}). Open the{" "}
              <button className="btn btn-link p-0 align-baseline" onClick={() => navigate(all_routes.reportCardDesigner)}>Report Card Designer</button>{" "}
              and save a template for this class.
            </div>
          ) : data.students.length === 0 ? (
            <div className="text-center text-muted py-5">No students in this section.</div>
          ) : (
            data.students.map(st => (
              <StudentCard
                key={st.enrollmentId}
                student={st}
                data={data}
                layers={layers}
                assetUrls={assetUrls}
                onNeedUrl={ensureAssetUrl}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

const StudentCard = ({
  student, data, layers, assetUrls, onNeedUrl,
}: {
  student: SectionCardStudent
  data: SectionReportCards
  layers: any[]
  assetUrls: Record<string, string>
  onNeedUrl: (key: string) => void
}) => {
  const ctx = {
    school: data.school,
    breakdown: data.breakdown,
    klass: data.class,
    section: data.section,
    tpl: { columns: {}, signatures: [], accentColor: undefined },
    rmsTpl: rmsTplOf(data),
    plan: planSubjects(layers, student),
    assetUrls,
    onNeedUrl,
    gradeLegend: data.gradeLegend,
  }
  return (
    <div className="rc-sheet-page">
      {layers.filter(l => !l.hidden && l.type !== "folder").map(l => {
        // RMS layers get the designer canvas's wrapper (Canvas.tsx): unclipped, and sized to content where
        // they have no stored height (styled subject cards always size to content).
        const rms = isRmsType(l.type)
        const styledTable = l.type === "rms_subject_table" && (l.variant ?? l._rmsProps?.variant) === "styled"
        const autoHeight = rms && (styledTable || !Number.isFinite(l.heightPct))
        return (
          <div
            key={l.id}
            style={{
              position: "absolute",
              left: l.x,
              top: l.y,
              width: l.w,
              height: autoHeight ? "auto" : l.h,
              opacity: l.opacity ?? 1,
              transform: l.rotation ? `rotate(${l.rotation}deg)` : undefined,
              overflow: rms ? "visible" : "hidden",
            }}
          >
            {renderPrintLayer(l, 1, student, ctx as any)}
          </div>
        )
      })}
    </div>
  )
}

export default ReportCardSheet
